import { FunctionComponent } from 'react'

import styled, { css } from 'styled-components'

import { desktopCSS } from '@measures/responsive'
import Credit from '@widgets/LeadImage/Credit'
import { CookWidget, JSONTypeForCookWidget } from '@widgets/types'
import ResponsiveImageV2 from '@components/ResponsiveImageV2'

import { LeadImageProps, TextWrapperProps, TitleProps } from './types'
import { IMAGE_RATIOS, IMAGE_WIDTHS } from './utils'

const Wrapper = styled.div`
  position: relative;
`

type StyledTextWrapperProps = Pick<TextWrapperProps, 'isWithImage'>

const TextWrapper = styled.div<StyledTextWrapperProps>`
  ${({
    theme: {
      spacing: { spacing24, spacing8, spacing32 },
    },
    isWithImage,
  }) => css`
    width: 100%;
    box-sizing: border-box;
    padding: ${spacing24} 0 ${spacing8} 0;
    ${desktopCSS(css`
      padding-bottom: ${spacing32};
      ${isWithImage &&
      css`
        position: absolute;
        left: 0;
        bottom: 0;
        padding: ${spacing32};
      `};
    `)}
  `}
`

type StyledHeadingProps = Pick<TitleProps, 'isWithImage'>

const Heading = styled.div<StyledHeadingProps>`
  ${({
    theme: {
      colors: { textDefault, staticWhite },
      typography: {
        catchword: {
          large1: { bundledCSS: catchwordLarge1 },
        },
      },
      spacing: { spacing8 },
    },
    isWithImage,
  }) => css`
    color: ${textDefault};
    margin-bottom: ${spacing8};
    ${catchwordLarge1};

    ${desktopCSS(css`
      color: ${isWithImage ? staticWhite : textDefault};
    `)}
    text-transform: uppercase;
  `}
`

type StyledTitleProps = Pick<TitleProps, 'isWithImage'>

const Title = styled.h2<StyledTitleProps>`
  ${({
    isWithImage,
    theme: {
      colors: { staticWhite, textDefault },
      typography: {
        headings: {
          xlarge: { bundledCSS: xlargeHeadings },
          xxxlarge: { bundledCSS: xxxlargeHeadings },
        },
      },
    },
  }) => css`
    margin: 0;
    display: block;
    ${xlargeHeadings};

    ${desktopCSS(css`
      color: ${isWithImage ? staticWhite : textDefault};
      ${xxxlargeHeadings};
    `)}
  `}
`

const LeadImage: FunctionComponent<LeadImageProps> = ({
  image,
  credit,
  title,
  catchword,
}) => (
  <Wrapper>
    {image ? (
      <ResponsiveImageV2
        src={image.src ?? ''}
        alt={image.alt ?? ''}
        ratios={IMAGE_RATIOS}
        crops={image.crops ?? {}}
        widths={IMAGE_WIDTHS}
        loading="eager"
      />
    ) : null}
    {!!credit && <Credit {...credit} />}
    {(!!catchword || !!title) && (
      <TextWrapper isWithImage={!!image}>
        {!!catchword && <Heading isWithImage={!!image}>{catchword}</Heading>}
        {!!title && <Title isWithImage={!!image}>{title}</Title>}
      </TextWrapper>
    )}
  </Wrapper>
)

const widget = {
  kind: ['lead-image'],
  component: LeadImage,
} as const satisfies CookWidget

export type WidgetType = typeof widget

export type JSONWidgetType = JSONTypeForCookWidget<WidgetType>

export default widget
